// TipTap rich text editor — Review ekranı orta pane.
// Markdown import/export basit: content string olarak tutulur; editor plain text
// markdown olarak render eder. Karar #1: TipTap + starter-kit (heading, bullet,
// bold, italic). M8 polish'te markdown-it tam round-trip eklenebilir.

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import MarkdownIt from "markdown-it";
import { useEffect, useRef } from "react";
import { shouldSyncEditor } from "../../lib/review-content-sync";

// markdown-it with GFM tables default
const mdParser = new MarkdownIt({ html: false, linkify: false, breaks: false });

export interface SectionEditorProps {
  content: string;
  readOnly: boolean;
  onChange: (content: string) => void;
}

export function SectionEditor({ content, readOnly, onChange }: SectionEditorProps): JSX.Element {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Editörün temsil ettiği son markdown (kendi yaydığı ya da dışarıdan basılan). Üst bileşen
  // yayılan markdown'ı `content` olarak geri verdiğinde `setContent` çağrılmaz — çağrılırsa
  // seçim sıfırlanır, imleç sona atlar (Finar 14.8).
  const lastEmittedRef = useRef<string | null>(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "tiptap-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: markdownToHtml(content),
    editable: !readOnly,
    // Selection değiştiğinde (ör. cursor tabloya girip çıkarken) toolbar butonlarının
    // `editor.isActive("table")` sonucuna göre render olması için — TipTap v3 default'u false.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => {
      const md = htmlToMarkdown(ed.getHTML());
      lastEmittedRef.current = md;
      onChangeRef.current(md);
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor",
        style: `min-height: 400px; padding: 1rem; border: 1px solid var(--color-border); border-radius: 8px; background: ${readOnly ? "var(--color-bg)" : "var(--color-surface)"}; outline: none; line-height: 1.7;`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    // HTML↔HTML karşılaştırması kullanılmaz: markdown-it sona `\n` ekler, liste `<li><p>` üretir —
    // ikisi hiç eşit olmadığı için her tuşta `setContent` çalışıyordu (kök neden).
    if (!shouldSyncEditor(lastEmittedRef.current, content)) return;
    lastEmittedRef.current = content;
    editor.commands.setContent(markdownToHtml(content), { emitUpdate: false });
  }, [content, editor]);

  useEffect(() => {
    // emitUpdate=false: TipTap `setEditable` varsayılan olarak `update` yayar; mount'ta normalize
    // edilmiş markdown `onChange`'e gider, bölüm hiç dokunulmadan "kaydedilmemiş" görünürdü.
    if (editor) editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  return (
    <div>
      {!readOnly && editor && (
        <div className="flex gap-2 mb-2 flex-wrap text-xs" style={{ color: "var(--color-text-2)" }}>
          <ToolbarButton
            active={editor.isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("heading", { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            B
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            I
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            •
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1.
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("table")}
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 2, cols: 3, withHeaderRow: true }).run()
            }
            title="Tablo ekle (2×3 başlıklı)"
          >
            ⊞ Tablo
          </ToolbarButton>
          {editor.isActive("table") && (
            <>
              <ToolbarButton
                active={false}
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Altına satır ekle"
              >
                +Satır
              </ToolbarButton>
              <ToolbarButton
                active={false}
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Sağa sütun ekle"
              >
                +Sütun
              </ToolbarButton>
              <ToolbarButton
                active={false}
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Tabloyu sil"
              >
                −Tablo
              </ToolbarButton>
            </>
          )}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={title}
      className="px-2 py-1 rounded border"
      style={{
        background: active ? "var(--color-primary)" : "transparent",
        color: active ? "var(--color-surface)" : "var(--color-text-2)",
        borderColor: "var(--color-border)",
        fontWeight: 600,
        minWidth: 28,
      }}
    >
      {children}
    </button>
  );
}

function markdownToHtml(md: string): string {
  if (!md) return "";
  return mdParser.render(md);
}

function htmlToMarkdown(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${html}</root>`, "text/html");
  const root = doc.querySelector("root");
  if (!root) return "";
  return serializeNode(root).trim();
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const inner = Array.from(el.childNodes).map(serializeNode).join("");
  switch (tag) {
    case "h1":
      return `# ${inner}\n\n`;
    case "h2":
      return `## ${inner}\n\n`;
    case "h3":
      return `### ${inner}\n\n`;
    case "h4":
      return `#### ${inner}\n\n`;
    case "strong":
    case "b":
      return `**${inner}**`;
    case "em":
    case "i":
      return `*${inner}*`;
    case "code":
      return `\`${inner}\``;
    case "p":
      return `${inner}\n\n`;
    case "br":
      return "\n";
    case "ul": {
      const items = Array.from(el.children)
        .filter((c) => c.tagName.toLowerCase() === "li")
        .map((li) => `- ${serializeChildren(li)}`)
        .join("\n");
      return `${items}\n\n`;
    }
    case "ol": {
      const items = Array.from(el.children)
        .filter((c) => c.tagName.toLowerCase() === "li")
        .map((li, i) => `${(i + 1).toString()}. ${serializeChildren(li)}`)
        .join("\n");
      return `${items}\n\n`;
    }
    case "li":
      return serializeChildren(el);
    case "table":
      return serializeTable(el);
    case "thead":
    case "tbody":
    case "tr":
    case "th":
    case "td":
      return inner; // satır/hücre serializeTable içinden okunur
    default:
      return inner;
  }
}

function serializeTable(el: Element): string {
  const rows = Array.from(el.querySelectorAll("tr"));
  if (rows.length === 0) return "";
  const headRow = rows[0]!;
  const headCells = Array.from(headRow.children).map((c) => serializeChildren(c));
  const body = rows.slice(1).map((tr) => {
    const cells = Array.from(tr.children).map((c) => serializeChildren(c));
    return `| ${cells.join(" | ")} |`;
  });
  const divider = `| ${headCells.map(() => "---").join(" | ")} |`;
  const lines = [`| ${headCells.join(" | ")} |`, divider, ...body];
  return `${lines.join("\n")}\n\n`;
}

function serializeChildren(el: Element): string {
  return Array.from(el.childNodes).map(serializeNode).join("").trim();
}
